import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

// Brand Theme Colors - Premium Palette
const theme = {
  primaryDark: "#1b325f",
  primaryBlue: "#282f81",
  accentCyan: "#11abea",
  bgLight: "#f4f7fb",
  cardBg: "#ffffff",
  textMain: "#334155",
  textMuted: "#64748b",
  borderLight: "#e2e8f0",
  successSoft: "#dcfce7",
  successDark: "#166534",
  dangerSoft: "#fee2e2",
  dangerDark: "#991b1b"
};

const VitalTypeMaster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [vitalList, setVitalList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const initialFormState = {
    vital_type_id: "",
    vital_type_name: "",
    unit: "",
    normal_range: "",
    status: "Active",
    _isExisting: false
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- 1. SEARCH LOGIC ---
  const searchVitals = async (text) => {
    setSearchText(text);
    if (text.length < 1) {
      setVitalList([]);
      setShowDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/vital-types/search?q=${text}&employee_id=${user?.id}`);
      const data = await res.json();
      setVitalList(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const selectVital = (item) => {
    setFormData({ ...item, _isExisting: true });
    setSearchText(`ID: ${item.vital_type_id} | ${item.vital_type_name}`);
    setShowDropdown(false);
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- 2. FORM ACTIONS (Auto-Increment Preview) ---
  const clearForm = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/vital-types?employee_id=${user?.id}`);
      const allData = await res.json();
      const lastId = Math.max(...allData.map(c => parseInt(c.vital_type_id) || 0), 0);
      
      setFormData({
        ...initialFormState,
        vital_type_id: (lastId + 1).toString()
      });
    } catch (err) {
      setFormData({ ...initialFormState });
    }
    setSearchText("");
    setIsDisabled(false);
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") clearForm();
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // --- 3. SAVE LOGIC ---
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!formData.vital_type_name) {
      return alert("Vital Type Name is required!");
    }

    const payload = { ...formData, created_by: user?.id };
    delete payload._isExisting;

    const method = formData._isExisting ? "PUT" : "POST";
    const url = method === "POST" 
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/vital-types" 
      : `https://hmsapi.emedhatechnologies.com/master/api/api/vital-types/${formData.vital_type_id}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert("Vital Type Saved Successfully!");
        if (method === "POST") clearForm();
      } else {
        alert("Error saving Vital Type (Check if name already exists)");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

  // --- 4. PRINT FORM ---
  const printForm = () => {
    if (!formData.vital_type_name) return alert("Please select a vital type to print.");
    
    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Vital Type - ${formData.vital_type_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #1b325f; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #64748b; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #11abea; font-size: 18px; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
            .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; width: 160px; color: #555; }
            .value { flex: 1; color: #000; font-weight: 500; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Clinical Configuration Department</p>
            </div>
            <div class="report-title">
              <h2>VITAL TYPE MASTER</h2>
              <p>Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Vital Parameter Profile</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Vital ID:</span> <span class="value">${formData.vital_type_id}</span></div>
            <div class="data-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
            <div class="data-item" style="grid-column: 1 / -1;"><span class="label">Vital Name:</span> <span class="value" style="font-weight: bold; color: #282f81;">${formData.vital_type_name}</span></div>
            <div class="data-item"><span class="label">Unit of Measure:</span> <span class="value">${formData.unit || 'N/A'}</span></div>
            <div class="data-item"><span class="label">Normal Range:</span> <span class="value">${formData.normal_range || 'N/A'}</span></div>
          </div>

          <div class="footer">
            System Generated Report - Emedha HMS
          </div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      {/* SEARCH SECTION */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Vital Type</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by ID or Name (e.g., Temperature, SpO2)"
            onChange={(e) => searchVitals(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && vitalList.length === 1) selectVital(vitalList[0]);
            }}
            autoFocus
          />
          {showDropdown && vitalList.length > 0 && (
            <div className="patient-dropdown">
              {vitalList.map((item) => (
                <div key={item.vital_type_id} className="patient-item" onClick={() => selectVital(item)}>
                  ID: {item.vital_type_id} | {item.vital_type_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}


{/* LUXURY FORM */}
      <form className="luxury-form">
        <div className="form-section-header">
          <h3>Vital Type Configuration</h3>
        </div>
        
   
      {/* 🔥 FIX: Using the responsive CSS class instead of inline styles */}
        <div className="form-grid vital-responsive-grid">
          
          {/* 1. ID Preview */}
          <div className="input-box read-only">
            <label>Vital ID</label>
            <input type="text" value={formData.vital_type_id} readOnly placeholder="Auto Generated" />
          </div>

          {/* 2. Vital Name */}
          <div className="input-box">
            <label>Vital Name <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              name="vital_type_name" 
              value={formData.vital_type_name} 
              onChange={handleChange} 
              disabled={isDisabled} 
              placeholder="e.g., Temperature" 
            />
          </div>

          {/* 3. Unit */}
          <div className="input-box">
            <label>Unit</label>
            <input 
              type="text" 
              name="unit" 
              value={formData.unit} 
              onChange={handleChange} 
              disabled={isDisabled} 
              placeholder="e.g., °F, bpm, %" 
            />
          </div>

          {/* 4. Normal Range */}
          <div className="input-box">
            <label>Normal Range</label>
            <input 
              type="text" 
              name="normal_range" 
              value={formData.normal_range} 
              onChange={handleChange} 
              disabled={isDisabled} 
              placeholder="e.g., 95–100%, 120/80" 
            />
          </div>

          {/* 5. Status */}
          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status} onChange={handleChange} disabled={isDisabled}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          
        </div>
      </form>
    </div>
  );
};

export default VitalTypeMaster;